import React from "react";
import usePageContent from "../../hooks/usePageContent";
import EditableSection from "../../components/CMS/EditableSection";
import PageHeaderEditor from "../../components/CMS/editors/PageHeaderEditor";
import ItemsEditor from "../../components/CMS/editors/ItemsEditor";

const ProgramsPage = ({ user }) => {
    const { sections, get, loading, error, refresh } =
        usePageContent("programs");

    const header = get("header");
    const programs = get("programs");
    const workflow = get("workflow");
    const cta = get("cta");

    const editable = user?.role === "admin";

    if (loading && sections.length === 0) {
        return <div>Loading Programs...</div>;
    }

    if (error && sections.length === 0) {
        return <div>Error: {error}</div>;
    }

    const getSection = key =>
        sections.find(s => s.section_key === key);

    const headerSection = getSection("header");
    const programsSection = getSection("programs");
    const workflowSection = getSection("workflow");
    const ctaSection = getSection("cta");

    return (
        <div className="programs-page">

            {/* =====================================
                HEADER
            ===================================== */}
            {headerSection && (
                <EditableSection
                    uniqueId={headerSection.unique_id}
                    title={headerSection.title}
                    data={header}
                    editable={editable}
                    Display={({ data }) => (
                        <section className="programs-hero">
                            <div className="container">

                                {data.eyebrow && (
                                    <div className="eyebrow">
                                        {data.eyebrow}
                                    </div>
                                )}

                                <h1>
                                    {data.title || "STEMX365 Programs"}
                                </h1>

                                {data.description && (
                                    <p className="hero-text">
                                        {data.description}
                                    </p>
                                )}

                            </div>
                        </section>
                    )}
                    Editor={PageHeaderEditor}
                    onSaved={refresh}
                />
            )}


            {/* =====================================
                PROGRAMS
            ===================================== */}
            {programsSection && (
                <EditableSection
                    uniqueId={programsSection.unique_id}
                    title={programsSection.title}
                    data={programs}
                    editable={editable}
                    Display={({ data }) => (
                        <section id="programs">

                            <div className="container">

                                <SectionHeader
                                    title={data.title || "Our Programs"}
                                    description={data.description}
                                />

                                <div className="program-grid">

                                    {(data.items || []).map(
                                        (item, i) => (

                                            <div
                                                className="program-card"
                                                key={i}
                                            >

                                                <div className="program-number">
                                                    PROGRAM{" "}
                                                    {String(i + 1).padStart(
                                                        2,
                                                        "0"
                                                    )}
                                                </div>

                                                {item.image ? (
                                                    <div
                                                        className="program-image"
                                                        style={{
                                                            backgroundImage:
                                                                `url(${item.image})`
                                                        }}
                                                    />
                                                ) : (
                                                    item.icon && (
                                                        <div className="program-icon">
                                                            {item.icon}
                                                        </div>
                                                    )
                                                )}

                                                <h3>
                                                    {item.title}
                                                </h3>

                                                <p>
                                                    {item.description}
                                                </p>

                                                {item.details && (
                                                    <p className="program-details">
                                                        {item.details}
                                                    </p>
                                                )}

                                                {item.url && (
                                                    <a
                                                        href={item.url}
                                                        className="learn-more"
                                                    >
                                                        {item.linkText ||
                                                            "Learn more →"}
                                                    </a>
                                                )}

                                            </div>
                                        )
                                    )}

                                </div>

                            </div>

                        </section>
                    )}
                    Editor={ItemsEditor}
                    onSaved={refresh}
                />
            )}


            {/* =====================================
                WORKFLOW
            ===================================== */}
            {workflowSection && (
                <EditableSection
                    uniqueId={workflowSection.unique_id}
                    title={workflowSection.title}
                    data={workflow}
                    editable={editable}
                    Display={({ data }) => (
                        <section className="platform-section">

                            <div className="container2">

                                <SectionHeader
                                    title={
                                        data.title ||
                                        "From Learning to Space"
                                    }
                                    description={data.description}
                                />

                                <div className="workflow">

                                    {(data.items || []).map(
                                        (item, i) => (

                                            <div
                                                className="workflow-step"
                                                key={i}
                                            >

                                                <div className="workflow-circle">
                                                    {item.number ||
                                                        String(i + 1).padStart(
                                                            2,
                                                            "0"
                                                        )}
                                                </div>

                                                <h4>
                                                    {item.title}
                                                </h4>

                                            </div>
                                        )
                                    )}

                                </div>

                            </div>

                        </section>
                    )}
                    Editor={WorkflowEditor}
                    onSaved={refresh}
                />
            )}


            {/* =====================================
                CTA
            ===================================== */}
            {ctaSection && (
                <EditableSection
                    uniqueId={ctaSection.unique_id}
                    title={ctaSection.title}
                    data={cta}
                    editable={editable}
                    Display={({ data }) => (
                        <section className="cta">

                            <div className="container">

                                {data.eyebrow && (
                                    <div className="eyebrow">
                                        {data.eyebrow}
                                    </div>
                                )}

                                <h2>
                                    {data.title || "Explore STEMX365"}
                                </h2>

                                {data.description && (
                                    <p>
                                        {data.description}
                                    </p>
                                )}

                                <div className="buttons">

                                    {(data.buttons || []).map(
                                        (button, i) => (

                                            <a
                                                key={i}
                                                href={button.url || "#"}
                                                className={
                                                    button.className ||
                                                    "btn btn-primary"
                                                }
                                            >
                                                {button.text}
                                            </a>
                                        )
                                    )}

                                </div>

                            </div>

                        </section>
                    )}
                    Editor={CTAEditor}
                    onSaved={refresh}
                />
            )}

        </div>
    );
};


/* =====================================
   SECTION HEADER
===================================== */

const SectionHeader = ({
    title,
    description
}) => (
    <div className="home-section-header">

        <h2>{title}</h2>

        {description && (
            <>
                <br />
                <p>{description}</p>
            </>
        )}

    </div>
);


/* =====================================
   WORKFLOW EDITOR
===================================== */

const WorkflowEditor = ({
    data,
    update,
    updateItem,
    addItem,
    removeItem
}) => (
    <div className="cms-editor">

        <label>Title</label>

        <input
            value={data.title || ""}
            onChange={e =>
                update("title", e.target.value)
            }
        />

        <label>Description</label>

        <textarea
            value={data.description || ""}
            onChange={e =>
                update("description", e.target.value)
            }
        />

        {(data.items || []).map((item, index) => (

            <div className="cms-item" key={index}>

                <div className="cms-item-header">

                    <strong>
                        Step {index + 1}
                    </strong>

                    <button
                        type="button"
                        className="cms-delete"
                        onClick={() =>
                            removeItem(index)
                        }
                    >
                        Delete
                    </button>

                </div>

                <label>Number</label>

                <input
                    value={item.number || ""}
                    onChange={e =>
                        updateItem(
                            index,
                            "number",
                            e.target.value
                        )
                    }
                />

                <label>Title</label>

                <input
                    value={item.title || ""}
                    onChange={e =>
                        updateItem(
                            index,
                            "title",
                            e.target.value
                        )
                    }
                />

            </div>
        ))}

        <button
            type="button"
            className="cms-add"
            onClick={() =>
                addItem({
                    number: String(
                        (data.items || []).length + 1
                    ).padStart(2, "0"),
                    title: ""
                })
            }
        >
            + Add Step
        </button>

    </div>
);


/* =====================================
   CTA EDITOR
===================================== */

const CTAEditor = ({
    data,
    update
}) => {

    const updateButton = (index, key, value) => {

        const buttons = [...(data.buttons || [])];

        buttons[index] = {
            ...buttons[index],
            [key]: value
        };

        update("buttons", buttons);
    };

    return (
        <div className="cms-editor">

            <label>Eyebrow</label>

            <input
                value={data.eyebrow || ""}
                onChange={e =>
                    update("eyebrow", e.target.value)
                }
            />

            <label>Title</label>

            <input
                value={data.title || ""}
                onChange={e =>
                    update("title", e.target.value)
                }
            />

            <label>Description</label>

            <textarea
                value={data.description || ""}
                onChange={e =>
                    update("description", e.target.value)
                }
            />

            {(data.buttons || []).map(
                (button, index) => (

                    <div
                        className="cms-item"
                        key={index}
                    >

                        <strong>
                            Button {index + 1}
                        </strong>

                        <label>Text</label>

                        <input
                            value={button.text || ""}
                            onChange={e =>
                                updateButton(
                                    index,
                                    "text",
                                    e.target.value
                                )
                            }
                        />

                        <label>URL</label>

                        <input
                            value={button.url || ""}
                            onChange={e =>
                                updateButton(
                                    index,
                                    "url",
                                    e.target.value
                                )
                            }
                        />

                        <label>CSS Class</label>

                        <input
                            value={
                                button.className || ""
                            }
                            onChange={e =>
                                updateButton(
                                    index,
                                    "className",
                                    e.target.value
                                )
                            }
                        />

                    </div>
                )
            )}

        </div>
    );
};

export default ProgramsPage;